import { Col, Row, classNames } from '@op/opds.opux';
import React, { type FC, useMemo } from 'react';
import { CoverageProductPriceSummary } from './CoverageProductPriceSummary';
import Installments from './Installments';
import styles from './PriceSummary.module.scss';
import { type Discount, DiscountType } from 'dto/CoveragePremium';
import i18n from 'i18n';
import { CustomTile } from 'individual/components/common/CustomTile';
import { type CustomerType } from 'individual/components/common/DiscountAction';
import PopupHintWrapper, {
  PopupParagraphs,
} from 'individual/components/PopupHintWrapper';
import {
  useSelectedPremiums,
  useAvailableCoverages,
  useMonthlyTotal,
  useAnnualTotal,
} from 'state/selectors/coverageSelectors';
import { useAppStore } from 'state/store';
import { getInitialState } from 'utils';
import { formatCentsToEurosWithSeparators } from 'utils/currency-utils';

export const PriceSummary: FC<{
  children?: string | JSX.Element | JSX.Element[] | (() => JSX.Element);
}> = ({ children }) => {
  const selectedSumInsuredPremiums = useSelectedPremiums();
  const availableCoverages = useAvailableCoverages();
  const selectedCoverages = useAppStore.use.selectedCoverages();
  const monthlyTotal = useMonthlyTotal();
  const annualTotal = useAnnualTotal();
  const { partyInformation, authenticatedCustomer } = getInitialState();

  const isPrimeCustomer = partyInformation?.primeCustomer ?? false;
  const isOwnerCustomer = partyInformation?.ownerCustomer ?? false;
  const isSimulatedPrimeCustomer =
    partyInformation?.simulatedPrimeCustomer ?? false;
  const isAuthenticated = authenticatedCustomer;

  const getCustomerType = (): CustomerType | undefined => {
    if (!isAuthenticated) return 'unAuthenticated';
    if (isSimulatedPrimeCustomer) return 'simulatedPrime';
    if (isOwnerCustomer) return 'owner';
    if (isPrimeCustomer) return 'prime';
    return undefined;
  };

  const popupHintKey = useMemo(() => {
    if (!isAuthenticated) {
      return 'priceSummary.priceTotalInfoTextUnauthenticated';
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    } else if (isOwnerCustomer || isPrimeCustomer || isSimulatedPrimeCustomer) {
      return 'priceSummary.priceTotalInfoTextAuthenticatedDiscount';
    } else {
      return 'priceSummary.priceTotalInfoTextAuthenticated';
    }
  }, [isAuthenticated, isOwnerCustomer, isPrimeCustomer]);

  // Check that the selectedSumInsuredPremiums is not an empty object
  if (Object.keys(selectedSumInsuredPremiums).length === 0) {
    return null;
  }

  const sortedSelectedCoverages = availableCoverages?.filter(
    (c) =>
      selectedCoverages.includes(c.coverageProduct) &&
      selectedSumInsuredPremiums[c.coverageProduct]
  );

  const allDiscounts = sortedSelectedCoverages
    ?.flatMap((c) => selectedSumInsuredPremiums[c.coverageProduct]?.discounts)
    .filter((discount): discount is Discount => !!discount);

  return (
    <CustomTile data-testid='pricesummary-container'>
      <>
        <h2>{i18n.t('common.price')}</h2>
        <dl className={classNames(styles.panel, styles.alignDLContent)}>
          {sortedSelectedCoverages?.map((coverage) => {
            const premium =
              selectedSumInsuredPremiums[coverage.coverageProduct];
            return (
              premium && (
                <CoverageProductPriceSummary
                  key={coverage.coverageProduct}
                  coverageProduct={coverage.coverageProduct}
                  premium={premium}
                  customerType={getCustomerType()}
                />
              )
            );
          })}
          <div className={styles.divider} aria-hidden='true'></div>
          <Row>
            <dt>
              <Col data-testid='price-popup-container'>
                {i18n.t('common.priceTotal')}
                <PopupHintWrapper
                  className={styles.popupHint}
                  ariaLabel={i18n.t('common.additionalInformationAriaLabel', {
                    context: i18n.t('common.priceTotal'),
                  })}>
                  <PopupParagraphs
                    texts={
                      i18n.t(popupHintKey, {
                        returnObjects: true,
                        discount:
                          allDiscounts?.find(
                            (discount) =>
                              discount.name ===
                              DiscountType.DISCRETIONARY_DISCOUNT
                          )?.percentage ?? 0,
                      }) as string[] | undefined
                    }
                  />
                </PopupHintWrapper>
              </Col>
            </dt>
          </Row>
          <Row>
            <dd>
              <Col
                className={classNames(
                  styles.right,
                  styles.total,
                  styles.noWrap
                )}
                data-testid='total-price-per-month'>
                {`${formatCentsToEurosWithSeparators(
                  monthlyTotal
                )} ${i18n.t('common.eurosPerMonth')}`}
              </Col>
            </dd>
          </Row>
          <Row>
            <dd>
              <Col
                className={classNames(styles.right, styles.noWrap)}
                data-testid='total-price-per-year'>
                {`${formatCentsToEurosWithSeparators(
                  annualTotal
                )} ${i18n.t('common.eurosPerYear')}`}
              </Col>
            </dd>
          </Row>
        </dl>
        <Installments
          key={
            selectedCoverages.join() +
            selectedCoverages
              .map((coverageProduct) =>
                selectedSumInsuredPremiums[
                  coverageProduct
                ]?.sumInsured.toString()
              )
              .join()
          }
        />
        {children}
      </>
    </CustomTile>
  );
};
